import * as React from "react";
import { ChevronDown } from "lucide-react";

const AccordionContext = React.createContext(null);
const AccordionItemContext = React.createContext(null);

const Accordion = ({ children, type = "single", collapsible = false }) => {
  const [openItem, setOpenItem] = React.useState(null);

  return (
    <AccordionContext.Provider value={{ openItem, setOpenItem, collapsible }}>
      <div>{children}</div>
    </AccordionContext.Provider>
  );
};

const useAccordion = () => {
  const context = React.useContext(AccordionContext);
  if (!context) throw new Error("Accordion components must be used within Accordion");
  return context;
};

const AccordionItem = ({ children, value, className = "" }) => {
  const { openItem } = React.useContext(AccordionContext);
  const isOpen = openItem === value;

  return (
    <AccordionItemContext.Provider value={{ value, isOpen }}>
      <div className={`${className} ${isOpen ? 'data-[state=open]' : ''}`} data-value={value} data-state={isOpen ? 'open' : 'closed'}>
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
};

const useAccordionItem = () => {
  const context = React.useContext(AccordionItemContext);
  if (!context) throw new Error("AccordionTrigger and AccordionContent must be used within AccordionItem");
  return context;
};

const AccordionTrigger = ({ children, className = "" }) => {
  const { setOpenItem, collapsible } = useAccordion();
  const { value, isOpen } = useAccordionItem();

  const handleClick = () => {
    if (isOpen && collapsible) {
      setOpenItem(null);
    } else {
      setOpenItem(value);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline ${className}`}
    >
      {children}
      <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
    </button>
  );
};

const AccordionContent = ({ children, className = "" }) => {
  const { isOpen } = useAccordionItem();

  if (!isOpen) return null;

  return (
    <div className={`pb-4 pt-0 ${className}`}>
      {children}
    </div>
  );
};

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
